import { ErrorLike } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { CacheControl } from '@tusbar/cache-control'
import { Cookies } from 'react-cookie'

import { ApolloClientProfile } from '@syconium/weeping-figs'

import { convertRequestToPreviewDirectives } from '../../../app/_config/PreviewDirectives.config'
import { CollectionMeta } from '../../../brunswick/components/pages/Collection/CollectionMeta'
import { CollectionView } from '../../../brunswick/components/pages/Collection/CollectionView'
import {
  COLLECTION_ITEM_MAX_ROW_SIZE,
  COLLECTION_ITEM_PAGE_SIZE,
  FILTERS_QUERY_PARAM,
  LegacyFilterQueryParam,
} from '../../../brunswick/constants'
import { FETCH_COLLECTION_LAYOUT } from '../../../lib/graphql/queries/FETCH_COLLECTION_LAYOUT'
import { GET_COLLECTION } from '../../../lib/graphql/queries/GET_COLLECTION'
import { cachePolicies } from '../../../lib/response-cache'
import { isBrowser } from '../../../lib/utils'
import { singleQueryParamValue } from '../../../lib/utils'
import { deriveCollectionItemFilterInput } from '../../../lib/utils/collection-filters'
import { MagnoliaPage, MagnoliaPageContext, MagnoliaPageInitialProps } from '../MagnoliaApp'

import { redirectLegacyFilterQueryParams } from './redirectLegacyFilterQueryParams'
import { SchemaTags } from './SchemaTags'

import type { CollectionGridSectionFragmentFragment } from '../../../__generated__/graphql/catalog/graphql'

type CollectionPageProps = MagnoliaPageInitialProps<{
  error: ErrorLike | undefined
  collectionHandle: string
  includeUnavailable: boolean
  isPreview: boolean
  launchTags?: string
}>

export const CollectionPage: MagnoliaPage<CollectionPageProps> = ({
  error: errorProp,
  collectionHandle,
  includeUnavailable,
  isPreview,
  launchTags,
}) => {
  const { data, error, loading } = useQuery(FETCH_COLLECTION_LAYOUT, {
    variables: { collectionHandle },
    context: { includeUnavailable, isPreview, launchTags },
  })

  const collectionView = data?.collectionView

  return (
    <>
      <CollectionMeta collectionView={collectionView ?? undefined} />

      <CollectionView
        collectionView={collectionView ?? undefined}
        loading={loading}
        error={error ?? errorProp}
      />
      <SchemaTags collectionView={collectionView ?? undefined} />
    </>
  )
}

CollectionPage.getInitialProps = function (pageContext: MagnoliaPageContext) {
  const collectionHandle: string = singleQueryParamValue(pageContext.query.collectionHandle, '')

  redirectLegacyFilterQueryParams({
    collectionFiltersQueryParam: pageContext.query[FILTERS_QUERY_PARAM],
    collectionHandle,
    isPlainPageQueryParam: pageContext.query['plain'],
    legacyCategoryQueryParam: pageContext.query[LegacyFilterQueryParam.Category],
    legacyColorQueryParam: pageContext.query[LegacyFilterQueryParam.Color],
    legacyFitQueryParam: pageContext.query[LegacyFilterQueryParam.Fit],
    pathname: pageContext.pathname,
    res: pageContext.res,
  })

  const {
    includeUnpublishedCmsContent: isPreview,
    includeUnavailableProducts: includeUnavailable,
    launchTags,
  } = convertRequestToPreviewDirectives({
    cookies: new Cookies(pageContext.req?.headers.cookie),
    url: pageContext.asPath ?? '',
  })

  return getInitialProps({
    collectionHandle,
    includeUnavailable,
    isPreview,
    pageContext,
    launchTags: launchTags ?? '',
  })
}

async function getInitialProps({
  collectionHandle,
  includeUnavailable,
  isPreview,
  pageContext,
  launchTags,
}: {
  collectionHandle: string
  includeUnavailable: boolean
  isPreview: boolean
  pageContext: MagnoliaPageContext
  launchTags: string
}): Promise<CollectionPageProps> {
  const pageProps: CollectionPageProps = {
    error: undefined,
    collectionHandle,
    includeUnavailable,
    isPreview,
    launchTags,
    responseCacheControl: cachePolicies.noCache,
  }

  /**
   * Render the page immediately by not blocking on getInitialProps and let the
   * page component run a fresh query client-side:
   */
  if (isBrowser()) return pageProps

  const client = pageContext.apolloClientFactory.for(ApolloClientProfile.FigsPublicSupergraph)
  const explicitNoCache = isPreview || includeUnavailable || !!launchTags.length

  /**
   * Prime the Apollo Client cache for SSR:
   */
  const { data, error } = await client.query({
    query: FETCH_COLLECTION_LAYOUT,
    variables: { collectionHandle },
    context: { includeUnavailable, isPreview, launchTags },
  })

  const editorializedCollections =
    data?.collectionView?.sections.reduce<
      Array<{
        editorializedCollection: CollectionGridSectionFragmentFragment['editorializedCollections'][number]
        filterConfig: CollectionGridSectionFragmentFragment['filterConfig']
      }>
    >((accumulatedEditorializedCollections, nextSection) => {
      if (nextSection.__typename === 'CollectionGridSection') {
        return accumulatedEditorializedCollections.concat(
          nextSection.editorializedCollections.map(editorializedCollection => {
            return {
              editorializedCollection,
              filterConfig: nextSection.filterConfig,
            }
          })
        )
      } else {
        return accumulatedEditorializedCollections
      }
    }, []) ?? []

  const collectionsData = await Promise.all(
    editorializedCollections.map((editorializedCollection, index) => {
      const { filterOptions } = deriveCollectionItemFilterInput({
        filterConfig: editorializedCollection.filterConfig,
        collectionFiltersQueryParam: pageContext.query[FILTERS_QUERY_PARAM],
        legacyCategoryQueryParam: pageContext.query[LegacyFilterQueryParam.Category],
        legacyColorQueryParam: pageContext.query[LegacyFilterQueryParam.Color],
        legacyFitQueryParam: pageContext.query[LegacyFilterQueryParam.Fit],
      })
      return client.query({
        query: GET_COLLECTION,
        variables: {
          collectionHandle: editorializedCollection.editorializedCollection.handle,
          nextPageDataOnly: false,
          filters: filterOptions,
          first: index === 0 ? COLLECTION_ITEM_PAGE_SIZE : COLLECTION_ITEM_MAX_ROW_SIZE,
        },
        context: { includeUnavailable, isPreview, launchTags },
      })
    })
  )

  if (error) {
    return {
      ...pageProps,
      error,
      responseCacheControl: cachePolicies.noCache,
    }
  }

  const hasProducts = !!collectionsData.find(
    o =>
      o.data?.productCatalogCollection.items.nodes &&
      o.data.productCatalogCollection.items.nodes.length > 0
  )

  if (!hasProducts && pageContext.res) {
    pageContext.res.statusCode = 404
  }

  return {
    ...pageProps,
    error,
    responseCacheControl: cachePolicy({
      error,
      explicitNoCache,
      hasProducts,
    }),
  }
}

function cachePolicy({
  error,
  explicitNoCache,
  hasProducts,
}: {
  error: ErrorLike | undefined
  explicitNoCache: boolean
  hasProducts: boolean
}): CacheControl {
  return explicitNoCache || error || !hasProducts
    ? cachePolicies.noCache
    : cachePolicies.volatileSharedHtmlPolicy
}
